import { DataTableColumnDef } from 'components/data-table';
import type { FormationSlot } from 'types/api-responses';
import { format0, format1 } from 'utils/number';
import style from './type-curve-wells-table.module.scss';

const renderBoolean = (v: boolean | null | undefined): string => {
  if (v === null || v === undefined) return '--';
  return v ? 'True' : 'False';
};

const renderInt = (v: number | null | undefined): string => {
  return v === null || v === undefined ? '--' : format0(v);
};

/**
 * A share of a well, shown as a percentage to one decimal. A plain LTD reads
 * 100.0%; the rows of one crossing well add up to it.
 *
 * @param {number | null | undefined} v - The share as a fraction of the well.
 * @return {string} The percentage, or a dash when unmeasured.
 */
const renderShare = (v: number | null | undefined): string => {
  return v === null || v === undefined ? '--' : `${format1(100 * v)}%`;
};

const renderIntFromString = (v: string | number | null | undefined): string => {
  if (v === null || v === undefined || v === '') return '--';
  const n = typeof v === 'string' ? Number(v) : v;
  return Number.isFinite(n) ? format0(n) : '--';
};

export const slotTableColumns: DataTableColumnDef<FormationSlot>[] = [
  {
    accessorKey: 'developmentAreaName',
    id: 'developmentAreaName',
    header: 'Development Area Name',
    size: 200,
    cell: (info) => {
      const name = info.getValue() as string;
      const href = info.row.original.resources?.developmentArea?.href;
      if (name && href) {
        return (
          <a
            href={href}
            className={style.wellLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            {name}
          </a>
        );
      }
      return name || '--';
    },
    meta: { type: 'text' },
  },
  {
    accessorKey: 'formation',
    id: 'formation',
    header: 'Formation',
    size: 120,
    // A well moved in depth whose landing is still running would otherwise show
    // the formation it left as if it were settled (Story 1).
    cell: (info) =>
      info.row.original.landingPending
        ? 'Landing…'
        : (info.getValue() as string | null) || '--',
    meta: { type: 'text' },
  },
  {
    accessorKey: 'slotId',
    id: 'slotId',
    header: 'Slot ID',
    size: 240,
    cell: (info) => (info.getValue() as string | null) || '--',
    meta: { type: 'text' },
  },
  {
    accessorKey: 'valued',
    id: 'valued',
    header: 'Valued',
    size: 100,
    cell: (info) => renderBoolean(info.getValue() as boolean | null),
    meta: { type: 'boolean' },
  },
  {
    accessorKey: 'uwi',
    id: 'uwi',
    header: 'UWI',
    size: 150,
    cell: (info) => {
      const uwi = info.getValue() as string;
      const href = info.row.original.resources?.well?.href;
      if (uwi && href) {
        return (
          <a
            href={href}
            className={style.wellLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            {uwi}
          </a>
        );
      }
      return uwi || '--';
    },
    meta: { type: 'text' },
  },
  {
    accessorKey: 'wellStatus',
    id: 'wellStatus',
    header: 'Well Status',
    size: 200,
    cell: (info) => (info.getValue() as string | null) || '--',
    meta: { type: 'text' },
  },
  {
    accessorKey: 'wellProducingLengthFt',
    id: 'wellProducingLengthFt',
    header: 'Well Producing Length (ft)',
    size: 230,
    cell: (info) => renderInt(info.getValue() as number | null),
    meta: { type: 'integer' },
  },
  {
    accessorKey: 'segmentProportionToWellProducingLength',
    id: 'segmentProportionToWellProducingLength',
    header: 'Segment Proportion of Well Length',
    size: 200,
    cell: (info) => renderShare(info.getValue() as number | null),
    meta: { type: 'decimal' },
  },
  {
    accessorKey: 'tvdFt',
    id: 'tvdFt',
    header: 'TVD (ft)',
    size: 110,
    cell: (info) => renderInt(info.getValue() as number | null),
    meta: { type: 'integer' },
  },
  {
    accessorKey: 'permitDate',
    id: 'permitDate',
    header: 'Permit Date',
    size: 120,
    meta: { type: 'date' },
  },
  {
    accessorKey: 'spudDate',
    id: 'spudDate',
    header: 'Spud Date',
    size: 120,
    meta: { type: 'date' },
  },
  {
    accessorKey: 'firstProductionDate',
    id: 'firstProductionDate',
    header: 'First Production Date',
    size: 200,
    meta: { type: 'date' },
  },
  {
    accessorKey: 'operator',
    id: 'operator',
    header: 'Operator',
    size: 220,
    cell: (info) => (info.getValue() as string) || '--',
    meta: { type: 'text' },
  },
  {
    accessorKey: 'cumOilBbl',
    id: 'cumOilBbl',
    header: 'Cum Oil (bbl)',
    size: 140,
    cell: (info) => renderInt(info.getValue() as number | null),
    meta: { type: 'integer' },
  },
  {
    accessorKey: 'cumGasMcf',
    id: 'cumGasMcf',
    header: 'Cum Gas (Mcf)',
    size: 140,
    cell: (info) => renderInt(info.getValue() as number | null),
    meta: { type: 'integer' },
  },
  {
    accessorKey: 'cumWaterBbl',
    id: 'cumWaterBbl',
    header: 'Cum Water (bbl)',
    size: 200,
    cell: (info) => renderInt(info.getValue() as number | null),
    meta: { type: 'integer' },
  },
  {
    accessorKey: 'oilEurBbl',
    id: 'oilEurBbl',
    header: 'Oil EUR (bbl)',
    size: 140,
    cell: (info) => renderIntFromString(info.getValue() as string | null),
    meta: { type: 'integer' },
  },
  {
    accessorKey: 'gasEurMcf',
    id: 'gasEurMcf',
    header: 'Gas EUR (Mcf)',
    size: 140,
    cell: (info) => renderIntFromString(info.getValue() as string | null),
    meta: { type: 'integer' },
  },
  {
    accessorKey: 'waterEurBbl',
    id: 'waterEurBbl',
    header: 'Water EUR (bbl)',
    size: 140,
    cell: (info) => renderIntFromString(info.getValue() as string | null),
    meta: { type: 'integer' },
  },
  {
    accessorKey: 'notes',
    id: 'notes',
    header: 'Notes',
    size: 200,
    cell: (info) => (info.getValue() as string | null) || '--',
    meta: { type: 'text' },
  },
];
